import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Plus, RefreshCw, UserSearch, X } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Badge } from 'components/ui/badge';
import { ToggleGroup, ToggleGroupItem } from 'components/ui/toggle-group';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'components/ui/tooltip';
import PlayerSearch from './PlayerSearch';
import {
  blogGenAddPlayer, blogGenDismissUnresolved, blogGenRefreshStats, blogGenUpdatePlayers,
} from '../../../services/api';
import { errMsg } from './util';

const confidenceBadge = (c) => {
  if (c.needs_review) return <Badge variant="warning">Needs a look</Badge>;
  if (c.method === 'reviewer' || c.method?.endsWith('/confirmed')) return <Badge variant="success">Confirmed</Badge>;
  if (c.confidence >= 0.99) return <Badge variant="success">Exact</Badge>;
  return <Badge variant="info">{c.method.split('/')[0]} {Math.round(c.confidence * 100)}%</Badge>;
};

const nextXpts = (c) => {
  const f = (c.fixtures || [])[0];
  if (!f) return '–';
  return `${f.xpts ?? '–'} (${f.opponent} ${f.is_home ? 'H' : f.is_home === false ? 'A' : '?'})`;
};

export default function StagePlayers({ postId, cards, unresolved, onCards, onExtraction, onJumpTs }) {
  const [busy, setBusy] = useState(false);
  const sorted = useMemo(
    () => [...(cards || [])].sort((a, b) => (b.needs_review - a.needs_review) || (a.role === 'excluded') - (b.role === 'excluded')),
    [cards]
  );
  const needs = sorted.filter((c) => c.needs_review && c.role !== 'excluded').length;
  const included = sorted.filter((c) => c.role !== 'excluded').length;

  const apply = async (fn, okMsg) => {
    setBusy(true);
    try {
      const next = await fn();
      if (next) onCards(next);
      if (okMsg) toast.success(okMsg);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const setRole = (c, role) => apply(() => blogGenUpdatePlayers(postId, [{ element: c.element, role }]));
  const confirm = (c) => apply(() => blogGenUpdatePlayers(postId, [{ element: c.element }]));
  const resolveTo = (c, p) => apply(() => blogGenUpdatePlayers(postId, [{ element: c.element, resolve_to: p.element }]), `Resolved to ${p.web_name}`);
  const remove = (c) => apply(() => blogGenUpdatePlayers(postId, [{ element: c.element, remove: true }]));
  const add = (p, raw = null) => apply(() => blogGenAddPlayer(postId, p.element, 'featured', raw), `Added ${p.web_name}`);
  const dismiss = (u) =>
    apply(async () => {
      await blogGenDismissUnresolved(postId, u.raw_name);
      onExtraction((ext) => ({ ...ext, unresolved: (ext.unresolved || []).filter((x) => x.raw_name !== u.raw_name) }));
      return null;
    });
  const refresh = () => apply(() => blogGenRefreshStats(postId), 'Stats refreshed');

  return (
    <TooltipProvider>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2 text-token-base">
          {needs > 0 ? <Badge variant="warning">{needs} need a look</Badge> : <Badge variant="success">All confirmed</Badge>}
          <Badge variant="secondary">{included} included</Badge>
          {unresolved?.length > 0 && <Badge variant="destructive">{unresolved.length} unresolved</Badge>}
        </div>
        <div className="flex gap-2">
          <PlayerSearch
            trigger={<Button size="sm" variant="outline" disabled={busy}><Plus /> Add player</Button>}
            onPick={(p) => add(p)}
          />
          <Button size="sm" variant="ghost" disabled={busy} onClick={refresh}><RefreshCw /> Refresh stats</Button>
        </div>
      </div>

      {unresolved?.length > 0 && (
        <div className="mb-3 rounded-md border border-destructive/40 bg-destructive/5 p-3">
          <p className="mb-2 text-token-base font-medium">Names the captions mentioned that we couldn't match</p>
          {unresolved.map((u) => (
            <div key={u.raw_name} className="flex flex-wrap items-center gap-2 py-1 text-token-base">
              <span className="font-medium">“{u.raw_name}”</span>
              {u.corrected_name && u.corrected_name !== u.raw_name && <span className="text-muted-foreground">→ {u.corrected_name}</span>}
              {u.team_hint && <Badge variant="outline">{u.team_hint}</Badge>}
              {(u.timestamps || []).slice(0, 2).map((t) => (
                <button key={t} type="button" className="text-primary underline" onClick={() => onJumpTs(t)}>{t}</button>
              ))}
              <PlayerSearch
                initialQuery={u.corrected_name || u.raw_name}
                team={u.team_hint}
                trigger={<Button size="sm" variant="outline" className="h-7"><UserSearch /> Find</Button>}
                onPick={(p) => add(p, u.raw_name)}
              />
              <Button size="sm" variant="ghost" className="h-7" onClick={() => dismiss(u)}><X /> Ignore</Button>
            </div>
          ))}
        </div>
      )}

      <div className="overflow-x-auto rounded-md border border-borderGrey">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Role</TableHead>
              <TableHead>Heard as</TableHead>
              <TableHead>Resolved player</TableHead>
              <TableHead>Match</TableHead>
              <TableHead className="text-right">Own%</TableHead>
              <TableHead className="text-right">Next xPts</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.map((c) => (
              <TableRow key={c.element} className={c.needs_review ? 'bg-warning/5' : c.role === 'excluded' ? 'opacity-50' : ''}>
                <TableCell>
                  <ToggleGroup type="single" size="sm" value={c.role} onValueChange={(v) => v && setRole(c, v)}>
                    <ToggleGroupItem value="featured" className="h-7 px-2 text-[11px]">Featured</ToggleGroupItem>
                    <ToggleGroupItem value="mentioned" className="h-7 px-2 text-[11px]">Mentioned</ToggleGroupItem>
                    <ToggleGroupItem value="excluded" className="h-7 px-2 text-[11px]">Exclude</ToggleGroupItem>
                  </ToggleGroup>
                </TableCell>
                <TableCell className="text-token-base">
                  <div>{c.raw_name || <span className="text-muted-foreground">(added)</span>}</div>
                  {c.corrected_name && c.corrected_name !== c.raw_name && (
                    <div className="text-muted-foreground">→ {c.corrected_name}</div>
                  )}
                  <div className="flex gap-1">
                    {(c.timestamps || []).slice(0, 3).map((t) => (
                      <button key={t} type="button" className="text-[11px] text-primary underline" onClick={() => onJumpTs(t)}>{t}</button>
                    ))}
                  </div>
                </TableCell>
                <TableCell>
                  <PlayerSearch
                    alternatives={c.alternatives || []}
                    initialQuery=""
                    trigger={
                      <button type="button" className="text-left">
                        <span className="font-medium underline decoration-dotted">{c.web_name}</span>{' '}
                        <Badge variant="outline" className="ml-1">{c.team_short} · {c.position}</Badge>
                      </button>
                    }
                    onPick={(p) => resolveTo(c, p)}
                  />
                  {c.availability_status && c.availability_status !== 'a' && (
                    <Tooltip>
                      <TooltipTrigger asChild><Badge variant="warning" className="ml-1">{c.availability_status.toUpperCase()}</Badge></TooltipTrigger>
                      <TooltipContent>{c.availability_news || 'Flagged by FPL'}</TooltipContent>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap items-center gap-1">
                    {confidenceBadge(c)}
                    {c.needs_review && (
                      <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" onClick={() => confirm(c)}>Looks right</Button>
                    )}
                  </div>
                  {c.needs_review && c.alternatives?.length > 0 && (
                    <div className="mt-1 text-[11px] text-muted-foreground">
                      or: {c.alternatives.slice(0, 3).map((a) => `${a.web_name} (${a.team_short})`).join(', ')}
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-right text-token-base">
                  {c.ownership_pct != null ? `${c.ownership_pct}%` : <span title={c.ownership_reason || ''}>n/a</span>}
                </TableCell>
                <TableCell className="text-right text-token-base">{nextXpts(c)}</TableCell>
                <TableCell>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => remove(c)} title="Remove row"><X /></Button>
                </TableCell>
              </TableRow>
            ))}
            {sorted.length === 0 && (
              <TableRow><TableCell colSpan={7} className="py-6 text-center text-muted-foreground">No players yet</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </TooltipProvider>
  );
}
